import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAppStore } from "../../store";
import {
  GAMES_HUB_PATH,
  KOBIKUUL_PROMO_DESTINATION,
  isKobikuulPromoDue,
  markKobikuulPromoDismissed,
  markKobikuulPromoShown,
} from "../../lib/kobikuul-promo";
import { ChatPromoCard, GamesPromoCard } from "./promo-cards";

/** Let the hero paint first — an instant interstitial on a fresh QR scan
 *  reads as an ad, and gets dismissed like one. */
const APPEAR_DELAY_MS = 700;

/** Past this much horizontal travel, releasing flicks the card away instead
 *  of snapping it back. Also cleared by a fast flick — see FLICK_VELOCITY. */
const COMMIT_DISTANCE_PX = 72;
/** px/ms — a short, fast swipe should count even if it never reached the
 *  distance threshold. Paired with FLICK_MIN_DISTANCE_PX so a fast twitch
 *  during a scroll cannot flip the deck. */
const FLICK_VELOCITY = 0.4;
const FLICK_MIN_DISTANCE_PX = 24;
/** Must match the card's transition duration below. */
const FLICK_MS = 260;
/** How much of the back card shows past the front one's right edge. Peeking
 *  sideways rather than downwards, so the stack points the same way the
 *  gesture does (Ken, 2026-09-04). Sized to stay inside the viewport on a
 *  375px screen once the 0.955 scale is accounted for. */
const BACK_CARD_PEEK_PX = 22;

type Slot = "chat" | "games";
const SLOTS: Slot[] = ["chat", "games"];

/**
 * Homepage promo deck — two stacked cards (the AI chat in front, the games
 * hub behind) shown to visitors for three days from their first sight of it
 * (MTN showcase programme, brochure QR codes land here). Swipe either way,
 * tap a dot, or use the arrow keys to bring the other card forward; the deck
 * only closes on X, "Maybe later" or Esc, so a swipe can never lose it.
 *
 * Members are skipped — they already have Kobikuul inside /main. Gate lives
 * in lib/kobikuul-promo.
 */
export default function KobikuulPromoModal() {
  const navigate = useNavigate();
  const { loggedIn } = useAppStore();
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(false);

  /** Index into SLOTS of the card currently in front. */
  const [frontIndex, setFrontIndex] = useState(0);
  /** Live horizontal offset of the front card while a finger is down. */
  const [dragX, setDragX] = useState(0);
  /** Non-null while the outgoing card is flying off. */
  const [flick, setFlick] = useState<-1 | 1 | null>(null);
  /** Suppresses transitions for the frame in which the cards change slots, so
   *  the outgoing card does not visibly slide back from off-screen. */
  const [swapping, setSwapping] = useState(false);
  /** True from the moment a gesture is judged horizontal. It flips the card's
   *  touch-action to none, which stops the browser scrolling the page
   *  underneath a swipe — with pan-y alone a diagonal swipe moved the card
   *  and the page at once, which is what made swiping feel unmoored. */
  const [dragging, setDragging] = useState(false);

  const pointer = useRef<{
    id: number;
    startX: number;
    startY: number;
    startedAt: number;
    axis: "undecided" | "horizontal" | "vertical";
  } | null>(null);

  useEffect(() => {
    if (loggedIn) return;
    if (!isKobikuulPromoDue()) return;

    const timer = window.setTimeout(() => {
      markKobikuulPromoShown();
      setOpen(true);
    }, APPEAR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [loggedIn]);

  // Animate in just after mount, so the transition has a from-state to run
  // from. A timer rather than requestAnimationFrame: rAF does not fire while
  // the tab is backgrounded, which would leave the card stuck at opacity-0.
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => setEntered(true), 16);
    return () => window.clearTimeout(timer);
  }, [open]);

  const dismiss = useCallback(() => {
    markKobikuulPromoDismissed();
    setOpen(false);
  }, []);

  /** Brings the other card forward, flicking the current one out in
   *  `direction` (-1 left, 1 right). */
  const advance = useCallback((direction: -1 | 1) => {
    setDragging(false);
    setFlick(direction);
    window.setTimeout(() => {
      setSwapping(true);
      setFlick(null);
      setDragX(0);
      setFrontIndex((index) => (index + 1) % SLOTS.length);
      // Re-enable transitions once the new slots have painted.
      window.setTimeout(() => setSwapping(false), 32);
    }, FLICK_MS);
  }, []);

  // Esc to close, arrows to flip. Deliberately no body scroll lock: this is a
  // promo, not a consent gate, and freezing the homepage under it felt like
  // being held hostage on a phone (Ken, 2026-09-04).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
      if (event.key === "ArrowLeft") advance(-1);
      if (event.key === "ArrowRight") advance(1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, dismiss, advance]);

  // Closes the deck for this view but records nothing: coming back to the
  // homepage should offer it again (Ken, 2026-09-04).
  const openChat = () => {
    setOpen(false);
    navigate(KOBIKUUL_PROMO_DESTINATION);
  };

  const openGames = () => {
    setOpen(false);
    navigate(GAMES_HUB_PATH);
  };

  const onPointerDown = (event: React.PointerEvent) => {
    if (flick !== null) return;
    pointer.current = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startedAt: event.timeStamp,
      axis: "undecided",
    };
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const state = pointer.current;
    if (!state || state.id !== event.pointerId) return;

    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;

    // Decide the gesture's axis once, then stick to it: a vertical drag has to
    // stay a page scroll, which is why the card sets touch-action: pan-y.
    if (state.axis === "undecided") {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      state.axis = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
      if (state.axis === "horizontal") {
        setDragging(true);
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // Pointer already released, or a synthetic event — the drag still
          // works, it just is not captured.
        }
      }
    }
    if (state.axis !== "horizontal") return;
    setDragX(dx);
  };

  const onPointerUp = (event: React.PointerEvent) => {
    const state = pointer.current;
    pointer.current = null;
    setDragging(false);
    if (!state || state.id !== event.pointerId) return;
    if (state.axis !== "horizontal") return;

    const dx = event.clientX - state.startX;
    const elapsed = Math.max(1, event.timeStamp - state.startedAt);
    const velocity = Math.abs(dx) / elapsed;

    const travelled = Math.abs(dx);
    const flicked =
      velocity > FLICK_VELOCITY && travelled > FLICK_MIN_DISTANCE_PX;
    if (travelled > COMMIT_DISTANCE_PX || flicked) {
      advance(dx < 0 ? -1 : 1);
      return;
    }
    setDragX(0);
  };

  if (!open) return null;

  const transition = swapping
    ? "transition-none"
    : "transition-all duration-[260ms] ease-out";

  return (
    <div
      // Above the homepage float buttons (z-9999) and its inline chat modal
      // (z-10000). `pointer-events-none` is what keeps the page usable: the
      // dim is decoration, so scrolls and taps outside the deck reach the
      // homepage underneath. Only the front card takes input.
      className={[
        // pb-12 on mobile leaves room for the dots below a bottom-anchored
        // deck; centred layouts have space either side already.
        "pointer-events-none fixed inset-0 z-[10001] flex items-end justify-center p-4 pb-14 sm:items-center sm:pb-4",
        "bg-gradient-to-t from-black/70 via-black/30 to-transparent sm:bg-black/60",
        "transition-opacity duration-300",
        entered ? "opacity-100" : "opacity-0",
      ].join(" ")}
    >
      {/* A single-cell grid holding both cards: the deck takes the height of
          the taller one and both stretch to it, so swapping never resizes
          anything (Ken, 2026-09-04 — different heights felt wonky). max-h-full
          gives the cards something definite to cap against; a percentage
          max-height against an auto-height parent resolves to none. */}
      <div className="relative grid max-h-full w-full max-w-sm">
        {SLOTS.map((slot, index) => {
          const isFront = index === frontIndex;
          const offset = isFront ? dragX : 0;
          const flickingAway = isFront && flick !== null;
          // The back card rises into the front position while the front one is
          // still flying off, so by the time the two swap slots the incoming
          // card is already where it belongs and nothing pops (Ken,
          // 2026-09-04).
          const arriving = !isFront && flick !== null;

          // Front card follows the finger with a slight tilt; the back card
          // sits to the right, smaller, so the deck reads as a stack.
          const transform = flickingAway
            ? `translateX(${flick * 140}%) rotate(${flick * 12}deg)`
            : isFront || arriving
              ? `translateX(${offset}px) rotate(${offset * 0.03}deg)`
              : // A slice of it stays visible past the front card's right
                // edge — the deck should look like a deck, and lean the way
                // you swipe.
                `translateX(${BACK_CARD_PEEK_PX}px) scale(0.955)`;

          return (
            <div
              key={slot}
              role="group"
              aria-label={
                slot === "chat" ? "Kobikuul AI chat" : "Kobikuul games"
              }
              aria-hidden={!isFront}
              onPointerDown={isFront ? onPointerDown : undefined}
              onPointerMove={isFront ? onPointerMove : undefined}
              onPointerUp={isFront ? onPointerUp : undefined}
              onPointerCancel={isFront ? onPointerUp : undefined}
              style={{
                transform,
                // Before a gesture is judged: vertical stays a page scroll and
                // the card only claims the horizontal axis. Once it IS judged
                // horizontal, the card claims everything for the rest of the
                // gesture, so the page cannot slide under a swipe.
                touchAction: isFront && dragging ? "none" : "pan-y",
                // Keeps a swipe that runs past the card from turning into a
                // pull-to-refresh or a rubber band.
                overscrollBehavior: "contain",
                zIndex: isFront ? 2 : 1,
                opacity: flickingAway ? 0 : isFront || entered ? 1 : 0,
              }}
              className={[
                // Same grid cell for both, so they are always the same size.
                "[grid-area:1/1] min-h-0",
                isFront ? "pointer-events-auto" : "",
                "flex flex-col overflow-hidden rounded-2xl bg-neutral-900 text-white shadow-2xl",
                // Lighter, not dimmer: a dimmed near-black card is
                // invisible against a dark backdrop. Ring gives it an edge.
                // Both fade out as the card arrives, so it does not brighten
                // once it is already in front.
                isFront || arriving ? "" : "brightness-[2.4] ring-1 ring-white/10",
                transition,
                entered ? "" : "translate-y-4 opacity-0",
              ].join(" ")}
            >
              {isFront && (
                <button
                  type="button"
                  onClick={dismiss}
                  aria-label="Close"
                  className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/70 backdrop-blur transition-colors hover:bg-white/20 hover:text-white"
                >
                  <X className="h-4 w-4" strokeWidth={2.5} />
                </button>
              )}

              {slot === "chat" ? (
                <ChatPromoCard
                  onCta={openChat}
                  onDismiss={dismiss}
                  interactive={isFront}
                />
              ) : (
                <GamesPromoCard
                  onCta={openGames}
                  onDismiss={dismiss}
                  interactive={isFront}
                />
              )}
            </div>
          );
        })}

        {/* Desktop has no swipe, so the dots are real controls, not decoration. */}
        <div className="pointer-events-auto absolute -bottom-9 left-0 right-0 flex items-center justify-center gap-2">
          {SLOTS.map((slot, index) => (
            <button
              key={slot}
              type="button"
              onClick={() => {
                if (index !== frontIndex) advance(1);
              }}
              aria-label={
                slot === "chat" ? "Show the chat card" : "Show the games card"
              }
              aria-current={index === frontIndex}
              className={[
                "h-2 rounded-full transition-all",
                index === frontIndex ? "w-5 bg-white" : "w-2 bg-white/40",
              ].join(" ")}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
